<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :baseZIndex="1200"
    :closable="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '32rem' }"
    :breakpoints="{ '960px': '92vw', '575px': '98vw' }"
    :pt="crcFormDialogPt"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div class="crc-form-dialog-header__icon bg-violet-50 dark:bg-violet-500/15 text-violet-600 dark:text-violet-400">
          <i class="pi pi-building text-base" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">Edit company</div>
        </div>
      </div>
    </template>

    <form @submit.prevent="handleSubmit">
      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-fields">
          <FloatLabel variant="on" class="w-full">
            <InputText
              id="employing-company-edit-name"
              v-model="draft.name"
              :disabled="saving"
              maxlength="190"
              autocomplete="off"
              :invalid="submitted && !draft.name?.trim()"
              class="w-full rounded-md"
            />
            <label for="employing-company-edit-name" class="text-sm font-medium">
              Company name <span class="text-red-500">*</span>
            </label>
          </FloatLabel>
          <small v-if="submitted && !draft.name?.trim()" class="text-xs text-red-500 pl-1">
            Company name is required.
          </small>

          <div class="flex flex-col gap-1.5">
            <label for="employing-company-edit-types" class="text-sm font-medium">
              Employment types <span class="text-red-500">*</span>
            </label>
            <MultiSelect
              id="employing-company-edit-types"
              v-model="draft.employment_type_codes"
              :options="typeOptions"
              option-label="label"
              option-value="value"
              display="chip"
              filter
              :disabled="saving"
              class="w-full"
              placeholder="Select employment types"
              :invalid="submitted && !draft.employment_type_codes.length"
            />
            <small v-if="submitted && !draft.employment_type_codes.length" class="text-xs text-red-500 pl-1">
              Select at least one employment type.
            </small>
            <small class="text-xs text-surface-500 dark:text-surface-400 pl-1">
              Controls which employment type dropdowns include this company.
            </small>
          </div>

          <p v-if="draft.employee_count > 0" class="text-xs text-surface-500 dark:text-surface-400 m-0 pl-1">
            Used by {{ draft.employee_count }} employee{{ draft.employee_count === 1 ? '' : 's' }}.
            Renaming updates existing employee records.
          </p>
        </div>
      </section>

      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-status">
          <div>
            <div class="crc-form-dialog-status__title">Active</div>
            <div class="crc-form-dialog-status__hint">
              Inactive companies are hidden from employee create/edit dropdowns.
            </div>
          </div>
          <ToggleSwitch v-model="draft.is_active" :disabled="saving" />
        </div>
      </section>

      <div class="crc-form-dialog-footer">
        <div class="crc-form-dialog-footer__actions">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            text
            :disabled="saving"
            @click="visibleProxy = false"
          />
          <Button type="submit" label="Save" icon="pi pi-check" :loading="saving" />
        </div>
      </div>
    </form>
  </Dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import MultiSelect from 'primevue/multiselect';
import ToggleSwitch from 'primevue/toggleswitch';
import { crcFormDialogPt } from '@/components/common/dialog/crcDialogPt.js';

defineOptions({ name: 'EmployingCompanyEditModal' });

const props = defineProps({
  visible: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  typeOptions: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:visible', 'save']);

const submitted = ref(false);

const draft = reactive({
  id: null,
  name: '',
  employment_type_codes: [],
  is_active: true,
  employee_count: 0,
});

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
});

const resetDraft = () => {
  draft.id = null;
  draft.name = '';
  draft.employment_type_codes = [];
  draft.is_active = true;
  draft.employee_count = 0;
  submitted.value = false;
};

const onClose = () => {
  if (!props.saving) resetDraft();
};

const openEdit = (row) => {
  draft.id = row?.id ?? null;
  draft.name = row?.name ?? row?.value ?? '';
  draft.employment_type_codes = [...(row?.employment_type_codes || [])];
  draft.is_active = row?.is_active !== false;
  draft.employee_count = Number(row?.employee_count ?? 0);
  submitted.value = false;
};

const handleSubmit = () => {
  submitted.value = true;
  const name = draft.name?.trim();
  if (!name || !draft.employment_type_codes.length) return;
  emit('save', {
    id: draft.id,
    name,
    employment_type_codes: [...draft.employment_type_codes],
    replace_type_links: 1,
    is_active: draft.is_active ? 1 : 0,
  });
};

defineExpose({ openEdit });
</script>
